import { FC, useCallback, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { FaEye, FaWind } from "react-icons/fa6";
import { HiOutlineEye } from "react-icons/hi2";
import { IoMdLocate } from "react-icons/io";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { MdOutlineInsertPhoto, MdOutlineModeEditOutline } from "react-icons/md";
import { Link } from "react-router-dom";
import Viewer from "react-viewer";
import { ActionIcon, Button, Text, Tooltip } from "rizzui";
import { toast } from "sonner";
import { ReactComponent as JDA } from "../../../assets/svg/jda.svg";
import DeletePopover from "../../../components/buttons/DeletePopover";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useIssuesStatus from "../../../hooks/api-calls/useIssuesStatus";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import Page from "../../../layout/Page/Page";
import { menuRoutes } from "../../../router/menu";
import { DocumentsService } from "../../../services/document/documentsService";
import { IssueService } from "../../../services/issue/issueService";
import { IssuesApiResponse } from "../../../type/entities/issues-type";
import cn from "../../../utils/classNames";
import { age, sex, speciesGroupSelector, windDirectionsSpanish, windSpeed } from "../../../utils/SelectorMapping";
import DrawCanvasFormModal from "../components/DrawCanvasFormModal";
import IssuesFormModal from "../components/IssuesFormModal";
import IssuesFilters from "./IssuesFilters";
import { ImportIssuesModal } from "./components/ImportIssuesModal";

interface IssuesListProps {
    setIsOpenModalForm: (value: boolean) => void;
    openModalForm: boolean;
    setIsOpenImport: (value: boolean) => void;
    openImport: boolean;
}


const columnOrder = [
    "dateReported",
    "dateEstimated",
    "windDirection",
    "windSpeed",
    "cloudCoverage",
    "coordinates",
    "windturbine",
    "distTurbine",
    "species",
    "numIndividuals",
    "sex",
    "age",
    "presenceCarrion",
    "stopNotified",
    "family",
    "id",
    "customId",
    'issueCode',
    "client",
    "park",
    "user",
    "status",
    "issuesPhotos",
    "imageCoordinates",
]


const IssuesList: FC<IssuesListProps> = ({ openModalForm, setIsOpenModalForm, openImport, setIsOpenImport }) => {

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false);
    const [issues, setIssues] = useState<any>(null);
    const [isViewerOpen, setIsViewerOpen] = useState(null);
    const [viewerImages, setViewerImages] = useState<any>(null);
    const [openDrawCanvasForm, setOpenDrawCanvasForm] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [viewerCoordinates, setViewerCoordinates] = useState<any>(null);
    const [isCoordinatesViewerOpen, setIsCoordinatesViewerOpen] = useState(null);

    // HOOKS

    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const service = new IssueService();
    const { getIssuesStatusList } = useIssuesStatus();

    // FETCH DATA

    const [data, loading, error, refetch] = useFetch(
        useCallback(async () => {
            const response = await service.listIssues(filters);
            return response.getResponseData();
        }, [filters])
    );

    // FUNCTIONS

    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR UNA INCIDENCIA
     * @EN DELETE AN ISSUE
     * @param id
     */
    //----------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteIssue(id)).getResponseData();
        if (response.success) {
            toast.success("Incidencia eliminada correctamente");
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //----------------------------------------------------------------------------------------------------------------------------

    const onCloseImport = () => {
        setIsOpenImport(false);
    }


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA TODAS LAS INCIDENCIAS SELECCIONADAS
     * @EN DELETES ALL SELECTED ISSUES
     * 
     * @param ids
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiIssues(ids)).getResponseData();
        if (response.success) {
            toast.success(response.message);
            refetch();
        } else {
            handleErrors(response);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ORDENA LOS DATOS PARA QUE COINCIDAN CON LA COLUMNA
     * @EN SORTS THE DATA TO MATCH THE COLUMN
     *
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            if (key === 'customId') {
                orderedRow[key] = row.id || '-';
            } else {
                orderedRow[key] = row[key];
            }
        });
        return orderedRow;
    });
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR DE ESTADO LA INCIDENCIA
     * @EN CHANGE THE STATUS OF THE ISSUE
     * 
     * @param id
     * @param status
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (id: string, status: string) => {
        const response = await (await service.changeStatusIssue(id, status)).getResponseData();
        if (response.success) {
            toast.success("Estado de la incidencia cambiada correctamente");
            refetch();
        } else {
            handleErrors(response);
            refetch();
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR A EXCEL
     * @EN EXPORT TO EXCEL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const exportToJDA = async (issueCode: string, id: string) => {
        const response = await (await service.exportToJDA(id)).getResponseData();
        if (response) {
            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'ficha_de_seguimiento_' + issueCode + '.pdf';
            a.click();
            window.URL.revokeObjectURL(url);
        } else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------



    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERAR MODAL
     * @EN CLOSE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose = () => {
        setIsOpenModalForm(false);
        setTimeout(() => {
            setIssues(null);
        }
            , 150);
    };
    //-----------------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERAR MODAL
     * @EN CLOSE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onCloseDrawCanvasForm = () => {
        setOpenDrawCanvasForm(false);
        setTimeout(() => {
            setIssues(null);
        }
            , 150);
    };
    //-----------------------------------------------------------------------------------------------------------------------

    const handleOpenViewer = async (issueRow: any) => {
        if (!issueRow.issuesPhotos || issueRow.issuesPhotos.length === 0) return;
        setIsLoadingImages(true);
        const images = await Promise.all(
            (issueRow.issuesPhotos || []).map(async (photo: any) => {
                const response = await new DocumentsService().renderDocument(photo.document.id);
                const file = new Blob([response?.getResponse()?.data], { type: 'image/' + photo.document.extension });
                return {
                    ...photo,
                    src: URL.createObjectURL(file),
                };
            })
        );
        setViewerImages(images);
        setIsViewerOpen(issueRow.id);
        setIsLoadingImages(false);
    };

    const handleOpenCoordinatesViewer = (issueRow: any) => {
        if (!issueRow.imageCoordinates) return;

        setIsLoadingImages(true);
        setViewerCoordinates([
            {
                src: `${issueRow.imageCoordinates}`,
                alt: "Plano"
            }
        ]);
        setIsCoordinatesViewerOpen(issueRow.id);
        setIsLoadingImages(false);
    };

    // RENDER

    return (
        <Page container="fluid">
            {data !== undefined && (
                <CustomTable
                    divClassName='max-h-table'
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    id={"issues-table"}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isSortable
                    columnsNoSortables={['customId', 'issuesPhotos']}
                    columnsNotShown={['customId', 'windSpeed']}
                    multiActions={[
                        {
                            label: 'Validar seleccionados',
                            render: (selectedRows: any, selectedRowsOriginal: any, reset: Function) => {
                                return (
                                    <Button
                                        variant="outline"
                                        color="primary"
                                        className="flex items-center border-green text-green"
                                        disabled={isValidating}
                                        onClick={async () => {
                                            try {
                                                setIsValidating(true);
                                                const response = (await service.validateSelectedIssues(selectedRows));
                                                if (response) {
                                                    toast.success('Incidencias validadas correctamente');
                                                    refetch();
                                                } else {
                                                    toast.error('Error al validar las incidencias');
                                                }
                                            } catch (error: any) {
                                                toast.error(error.message);
                                            } finally {
                                                setIsValidating(false);
                                                reset();
                                            }
                                        }}
                                    >
                                        <IoCheckmarkDoneSharp className="h-[20px] w-[20px] mr-2" />
                                        {isValidating ? <FaSpinner className="animate-spin h-[20px] w-[20px] mr-2" /> : <span>Validar</span>}
                                    </Button>
                                )
                            }
                        }
                    ]}
                    overrideColumns={[
                        {
                            key: 'issueCode',
                            customHeader: 'Código',
                            render: (row: any) => {
                                const issueCode = row.row.original.issueCode || '-';

                                if (userCan('get', 'issues')) {
                                    return (
                                        <a href={`${menuRoutes.issues.path}/${row.row.original.customId}/profile/${menuRoutes.issues.profile.info}`} className='text-primary hover:text-primary hover:underline'>
                                            {issueCode}
                                        </a>
                                    );
                                }

                                return <span>{issueCode}</span>;
                            }
                        },
                        {
                            key: 'client',
                            render: (row: any) => {
                                return row.row.original.park?.client?.name || '-';
                            }
                        },
                        {
                            key: 'park',
                            customHeader: 'Parque Eólico',
                            render: (row: any) => {
                                return row.row.original.park?.name || '-';
                            }
                        },
                        {
                            key: 'windturbine',
                            customHeader: 'Aero',
                            render: (row: any) => {
                                return row.row.original.windturbine?.alias || '-';
                            }
                        },
                        {
                            key: 'user',
                            customHeader: 'Observador',
                            render: (row: any) => {
                                return row.row.original.user?.name + ' ' + row.row.original.user.lastName || '-';
                            }
                        },
                        {
                            key: 'species',
                            customHeader: 'Especie',
                            render: (row: any) => {
                                return (
                                    <div className="italic">
                                        {row.row.original.species?.scientificName || '-'}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'presenceCarrion',
                            customHeader: 'Carroña',
                            render: (row: any) => {
                                return row.row.original.presenceCarrion == 'yes' ? 'Sí' : 'No';
                            }
                        },
                        {
                            key: 'status',
                            render: (row: any) => {
                                const issuesStatus = row.row.original?.status;
                                const issuesId = row.row.original?.customId;
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.customId + issuesStatus.id}>
                                        {(issuesId && issuesStatus) ? (
                                            <StatusDropdown
                                                divClassName='fs-table'
                                                selectedClassName='fs-table'
                                                optionsClassName='fs-table'
                                                entityId={row.row.original?.customId}
                                                title="¿Estas seguro de cambiar el estado de la Incidencia?"
                                                statesOptions={getIssuesStatusList()}
                                                currentState={issuesStatus}
                                                disabled={!userCan('edit', 'issues')}
                                                handleStateChange={(entityId: string, statusId: string) => handleChangeStatus(issuesId, statusId)}
                                                hasComments={false}
                                            />
                                        ) : "N/A"}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'issuesPhotos',
                            customHeader: 'Fotos',
                            render: (row: any) => (
                                <p className="flex items-center gap-2">
                                    <MdOutlineInsertPhoto
                                        className="h-4 w-4"
                                        size={20}
                                        onClick={() => handleOpenViewer(row.row.original)}
                                    />
                                    <span>{row.row.original.issuesPhotos?.length || 0}</span>
                                    {row.row.original.issuesPhotos?.length !== 0 ? <Viewer
                                        visible={isViewerOpen === row.row.original.id}
                                        onClose={() => setIsViewerOpen(null)}
                                        images={viewerImages?.map((img: any) => ({ src: img.src, alt: img.document.name }))}
                                    />
                                        : null}
                                </p>
                            )
                        },
                        {
                            key: 'coordinates',
                            customHeader: 'Coordenadas',
                            render: (row: any) => {
                                const [h, x, y] = row.row.original.coordinates.split(',');
                                return (
                                    <>
                                        <p key="h">H: {h}S</p>
                                        <p key="x">X: {x}</p>
                                        <p key="y">Y: {y}</p>
                                    </>
                                );
                            }
                        },
                        {
                            key: 'imageCoordinates',
                            customHeader: 'Coordenadas del Cadaver',
                            render: (row: any) => (
                                <div key="photos" className="flex items-center gap-2">
                                    {row.row.original.imageCoordinates !== null ? (
                                        <p className="flex items-center gap-2">
                                            Cadaver localizado
                                            <FaEye
                                                className="h-3 w-3"
                                                size={20}
                                                onClick={() => {
                                                    handleOpenCoordinatesViewer(row.row.original);
                                                }}
                                            />
                                        </p>
                                    ) : (
                                        <p>Cadaver no localizado</p>
                                    )}
                                    {row.row.original.imageCoordinates !== null && (
                                        <Viewer
                                            visible={isCoordinatesViewerOpen === row.row.original.id}
                                            onClose={() => setIsCoordinatesViewerOpen(null)}
                                            images={viewerCoordinates?.map((img: any) => ({ src: img.src, alt: img.alt }))}
                                        />
                                    )}
                                </div>
                            )
                        },
                        {
                            key: 'numIndividuals',
                            customHeader: 'Num',
                            render: (row: any) => {
                                return row.row.original?.numIndividuals || '-';
                            }
                        },
                        {
                            key: 'windDirection',
                            customHeader: 'D_VTO',
                            render: (row: any) => {
                                return (
                                    <div className="flex flex-col gap-1">
                                        <Text className="text-center capitalize flex items-center">
                                            <FaWind className="h-3 w-3 me-1 text-primary" size={10} />
                                            {windDirectionsSpanish.find((item) => item.value == row.row.original.windDirection)?.label || '-'},
                                        </Text>
                                        <Text className="text-center fs-table font-bold text-gray-500">
                                            {windSpeed.find((item) => item.value == row.row.original.windSpeed)?.label.split('-')[1].trim() || '-'}
                                        </Text>
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'cloudCoverage',
                            customHeader: 'COB',
                            render: (row: any) => {
                                return (
                                    <div className="flex flex-col gap-2">
                                        <Text className="text-center text-capitalize">
                                            {row.row.original.cloudCoverage + '%'}
                                        </Text>
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'distTurbine',
                            customHeader: 'Dist (m)',
                            render: (row: any) => {
                                return (
                                    <div className="flex flex-col gap-2">
                                        <Text className="text-capitalize">
                                            {row.row.original.distTurbine + ' m'}
                                        </Text>
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'sex',
                            render: (row: any) => {
                                return (
                                    <div className="flex flex-col gap-2">
                                        <Text className="text-capitalize">
                                            {sex.find((item) => item.value == row.row.original.sex)?.label || '-'}
                                        </Text>
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'age',
                            render: (row: any) => {
                                return (
                                    <div className="flex flex-col gap-2">
                                        <Text className="text-capitalize">
                                            {age.find((item) => item.value == row.row.original.age)?.label || '-'}
                                        </Text>
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'stopNotified',
                            render: (row: any) => {
                                return (
                                    <div className="flex flex-col gap-2">
                                        <Text className="text-capitalize">
                                            {row.row.original.stopNotified ? 'Si' : 'No'}
                                        </Text>
                                    </div>
                                )
                            }
                        },
                        {
                            key: 'family',
                            render: (row: any) => {
                                return (
                                    <div className="flex flex-col gap-2">
                                        <Text className="text-capitalize">
                                            {speciesGroupSelector.find((option: any) => option.value === row.row.original.species.family)?.label || '-'}
                                        </Text>
                                    </div>
                                )
                            }
                        }
                    ]}
                    actions={[
                        {
                            label: 'Exportar a Junta de Andalucía',
                            permissions: { group: 'issues', permission: 'download_pdf' },
                            render: (row: any) => {
                                return (<>
                                    {row?.status?.name == 'Validada' && (
                                        <Tooltip
                                            size="sm"
                                            content={'Exportar al formato de la Junta de Andalucía'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <ActionIcon
                                                color="primary"
                                                onClick={() => {
                                                    exportToJDA(row.issueCode, row.customId);
                                                }}
                                                className={cn(['bg-white border-secondary text-black w-5 h-5'])}
                                            >
                                                <JDA className="h-3 w-3" />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                </>
                                );
                            },
                        },
                        {
                            label: 'Locazacion del Cadaver',
                            permissions: { group: 'issues', permission: 'set_location' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Localizar Cadaver'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            onClick={() => {
                                                setOpenDrawCanvasForm(true);
                                                setIssues(row.customId);
                                            }}
                                        >
                                            <IoMdLocate className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'View',
                            permissions: { group: 'issues', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver Incidencia'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.issues.path}/${row.customId}/profile/${menuRoutes.issues.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            >
                                                <HiOutlineEye className="h-3 w-3" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'issues', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar Incidencia'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            onClick={() => {
                                                setIsOpenModalForm(true);
                                                setIssues(row.customId);
                                            }}
                                        >
                                            <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'issues', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar incidencia'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar incidencia`}
                                                actionIconClassName='w-5 h-5'
                                                description={`¿Estás seguro de que deseas eliminar a ${row.issueCode}?`}
                                                onDelete={() => handleDelete(row.customId)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as IssuesApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}

                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de Incidencias'}>
                <IssuesFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>

            <ImportIssuesModal
                isOpen={openImport}
                onClose={onCloseImport}
                refetch={refetch}
            />

            <IssuesFormModal
                isOpen={openModalForm}
                onClose={onClose}
                issues={issues}
                refetch={refetch}
            />

            <DrawCanvasFormModal
                isOpen={openDrawCanvasForm}
                onClose={onCloseDrawCanvasForm}
                refetch={refetch}
                issue={issues}
            />

        </Page>
    )
}

export default IssuesList;
